'use client';

import React, { useContext, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

interface Props {
  hostname: string;
}

interface Context {
  hostname: string;
}

interface UseUrlResult {
  hostname: string;
  origin: string;
  path: string;
  url: string;
}

const UrlContext = React.createContext<Context | undefined>(undefined);

export const UrlProvider: React.FunctionComponent<React.PropsWithChildren<Props>> = ({ children, hostname }) => {
  const value = useMemo(() => ({ hostname }), [hostname]);

  return <UrlContext.Provider value={value}>{children}</UrlContext.Provider>;
};

export const useUrl = (): UseUrlResult => {
  const value = useContext(UrlContext);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  if (!value) {
    throw new Error('useUrl can not be used outside of <UrlContext>');
  }

  const searchParamsString = searchParams.toString();
  const path = pathname + (searchParamsString === '' ? '' : `?${searchParamsString}`);
  const origin = `https://${value.hostname}`;
  const url = `${origin}${path}`;

  return {
    hostname: value.hostname,
    origin,
    path,
    url,
  };
};
